import { ResponsiveLine, Serie } from "@nivo/line"
import {
	Card as CardContainer,
	CardItem,
	Chip,
	ChipGroup,
	Heading,
} from "@walmart-web/livingdesign-components"
import React from "react"
import { useIntl } from "react-intl"
import "./index.scss"
import { TimelineProps } from "./interfaces"
import { CHIP_TRANSLATIONS, getAxisLeft } from "./utils"
import TimeLineFooter from "./TimeLineFooter"
import TimeLineTooltip from "./TimeLineTooltip"
import { DAILY_INTERVAL, WEEKLY_INTERVAL } from "./constant"
import TimeLineSliceTooltip from "./TimeLineSliceTooltip"
import { TIMELINE_THEME } from "./theme.style"

const Timeline: React.FC<TimelineProps> = (props) => {
	const {
		title,
		subtitle,
		chartData,
		colors,
		yAxisLegend = "",
		xAxisLegend = "",
		contentFooter,
		tooltip,
		setInterval,
		interval,
		isDailyEnabled = true,
		isWeeklyEnabled = true,
		isMonthlyEnabled = true,
		enableGridX = true,
		enableGridY = true,
		slice = false,
		isPercentage = false,
		additionalButton,
	} = props
	const intl = useIntl()

	const handleChartData = (interval: string) => {
		setInterval(interval)
	}

	const getIsButtonEnabled = (interval: string) => {
		if (interval === DAILY_INTERVAL) {
			return isDailyEnabled
		}
		if (interval === WEEKLY_INTERVAL) {
			return isWeeklyEnabled
		}
		return isMonthlyEnabled
	}

	const renderChip = (intervalSelected: string, message: string) => {
		const isEnabled = getIsButtonEnabled(intervalSelected)
		const isIntervalSelected = interval === intervalSelected

		return (
			<Chip
				key={intervalSelected}
				selected={isIntervalSelected}
				disabled={!isEnabled}
				onClick={() => handleChartData(intervalSelected)}
				data-testid="chip"
			>
				{intl.formatMessage({ id: message })}
			</Chip>
		)
	}

	return (
		<CardContainer
			className="timeline-container"
			data-testid="timeline-container"
		>
			<CardItem className="timeline-header">
				<Heading UNSAFE_className="font-24">{title}</Heading>
				<div className="timeline-interval-options">
					<ChipGroup>
						{CHIP_TRANSLATIONS.map((chip) => {
							return renderChip(chip.interval, chip.id)
						})}
					</ChipGroup>
					{additionalButton}
				</div>
			</CardItem>
			<CardItem className="timeline-subheader">
				<div className="subtitle">{subtitle ? subtitle : <></>}</div>
			</CardItem>
			<div className="chart-container">
				<ResponsiveLine
					data-testid="responsive-line"
					data={chartData?.data as Serie[]}
					margin={{
						top: 50,
						right: 50,
						bottom: 60,
						left: 60,
					}}
					yScale={{
						type: "linear",
						max: "auto",
					}}
					axisLeft={{
						format: (value) => {
							return getAxisLeft(value, isPercentage, intl)
						},
						legend: yAxisLegend,
						legendPosition: "middle",
						legendOffset: -50,
						tickSize: 4,
						tickPadding: 0,
					}}
					enableGridX={enableGridX}
					enableGridY={enableGridY}
					colors={colors}
					axisBottom={{
						tickRotation: 0,
						legend: xAxisLegend,
						legendOffset: 50,
						legendPosition: "middle",
					}}
					theme={TIMELINE_THEME}
					markers={[
						{
							axis: "x",
							value: 1,
							lineStyle: { stroke: "black", strokeWidth: 2 },
						},
						{
							axis: "y",
							value: 0,
							lineStyle: { stroke: "black", strokeWidth: 2 },
						},
					]}
					isInteractive={true}
					enablePoints={false}
					animate={true}
					useMesh={true}
					tooltip={(props) => (
						<TimeLineTooltip props={props} content={tooltip} />
					)}
					pointLabelYOffset={0}
					enableSlices={slice}
					sliceTooltip={(props) => (
						<TimeLineSliceTooltip props={props} content={tooltip} />
					)}
				/>
			</div>
			<CardItem className="timeline-footer">
				<TimeLineFooter content={contentFooter} />
			</CardItem>
		</CardContainer>
	)
}

export default Timeline
